import ListGroup from '@pelckmans/elementis/components/list-group';
import Section from '@pelckmans/elementis/components/section';
import Toggle from '@pelckmans/elementis/components/toggle';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Medialink } from '../../../../../api/medialink';
import { SlideSet } from '../../../../../api/slide-set';
import { UserMaterial } from '../../../../../api/user-material';
import { ANALYTICS_EVENT_SUBLOCATIONS, type AnalyticsEventSubLocation } from '../../../../../constants/analyticsEvents';
import EmptyState from '../../../../../shared-components/EmptyState';
import LoadingState from '../../../../../shared-components/LoadingState';
import ModuleContext from '../../../_contexts/ModuleContext';
import useSlideSets from '../../../hooks/useSlideSets';
import MedialinksContext from '../../_contexts/MedialinksContext';
import SelectionContext from '../../_contexts/SelectionContext';
import TableOfContentsContext from '../../_contexts/TableOfContentsContext';
import UserMaterialContext from '../../_contexts/UserMaterialContext';
import MedialinkList from '../MedialinkList';
import SlideSetList from '../SlideSetList';
import { groupByContentType } from '../utils';
import AddMaterialButton from './AddMaterialButton';
import UserMaterialList from './UserMaterialList';
import { useWithLicenseCheck } from './useWithLicenseCheck';
import useModuleAccess from '../../../../../hooks/useModuleAccess';
import { FEATURE_ACCESS } from '../../../../../core/access/module-access';

const BASE = 'PAGES.TOC_LEVEL.SECTIONS.TEACHER.MATERIAL';

type MaterialSectionProps = {
  title: string;
  medialinks: Medialink[];
  userMaterial?: UserMaterial[];
  slideSets?: SlideSet[];
  actions?: boolean;
  isLoading: boolean;
  subLocationId: AnalyticsEventSubLocation;
  isExerciseModule?: boolean;
  emptyMessage?: string;
};

function MaterialSection({
  title,
  medialinks,
  userMaterial = [],
  slideSets = [],
  actions = true,
  isLoading,
  subLocationId,
  isExerciseModule = false,
  emptyMessage,
}: MaterialSectionProps) {
  const { t } = useTranslation();
  const { selectedModule } = useContext(ModuleContext);
  const { materialAccess } = useModuleAccess(selectedModule);
  const canSelectMaterial = materialAccess !== FEATURE_ACCESS.HIDDEN;
  const { showSelection, toggleShowSelection } = useContext(SelectionContext);
  const { selectedNode } = useContext(TableOfContentsContext);
  const { fetchSlideSets } = useSlideSets();
  const { licenseCheck } = useWithLicenseCheck();

  const handleToggleShowSelection = licenseCheck(toggleShowSelection);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchSlideSets(false);
      }
    };

    window.addEventListener('visibilitychange', onVisibilityChange);

    return () => window.removeEventListener('visibilitychange', onVisibilityChange);
  }, [fetchSlideSets]);

  if (!selectedNode) return null;

  const medialinksSortedByCategory = groupByContentType(medialinks);
  const isEmpty = !isLoading && userMaterial.length === 0 && slideSets.length === 0 && medialinks.length === 0;

  const hasContent = !(isEmpty || isLoading);

  function renderContent() {
    if (isLoading) {
      return <LoadingState variant="list" aria-label={t(`${BASE}.LOADING.A11Y_LABEL`)} />;
    }

    if (isEmpty) {
      if (isExerciseModule) {
        return null; // handled by section empty message;
      } else {
        return (
          <EmptyState
            variant="list"
            headerTitle={t(`${BASE}.EMPTY_DIALOG.TITLE`)}
            bodyText={t(`${BASE}.EMPTY_DIALOG.TEXT`)}
            cta={<AddMaterialButton />}
          />
        );
      }
    }

    return (
      <ListGroup>
        {userMaterial.length > 0 && (
          <UserMaterialList
            key="user-materials"
            userMaterial={userMaterial}
            title={t(`${BASE}.SUBTITLE_OWN_MATERIAL`)}
          />
        )}
        {slideSets.length > 0 && <SlideSetList key="slide-sets" title={t(`${BASE}.SUBTITLE_SLIDE_SETS`)} />}
        {Object.keys(medialinksSortedByCategory).map(category => (
          <MedialinkList
            key={category}
            medialinks={medialinksSortedByCategory[category]}
            title={category}
            subLocationId={subLocationId}
          />
        ))}
      </ListGroup>
    );
  }

  return (
    <Section
      title={title}
      actions={(isExerciseModule || hasContent) && actions && <AddMaterialButton />}
      toggle={
        hasContent &&
        canSelectMaterial && (
          <Toggle label={t(`${BASE}.TOGGLE_SELECT`)} checked={showSelection} onChange={handleToggleShowSelection} />
        )
      }
      actionGroupRole={hasContent ? 'group' : undefined}
      actionGroupAriaLabel={hasContent ? t(`${BASE}.BUTTON_GROUP.A11Y_LABEL`) : undefined}
      emptyMessage={isExerciseModule ? emptyMessage : undefined}
    >
      {renderContent()}
    </Section>
  );
}

function splitExerciseMedialinks(medialinks: Medialink[]) {
  return medialinks.reduce(
    (acc: { exerciseMedialinks: Medialink[]; nonExerciseMedialinks: Medialink[] }, medialink) => {
      if (medialink.contentType.isExercise) {
        acc.exerciseMedialinks.push(medialink);
      } else {
        acc.nonExerciseMedialinks.push(medialink);
      }
      return acc;
    },
    { exerciseMedialinks: [], nonExerciseMedialinks: [] },
  );
}

export default function MaterialSections() {
  const { selectedModule } = useContext(ModuleContext);
  const { medialinks, isLoading: isLoadingMediaLinks } = useContext(MedialinksContext);
  const { userMaterial, isLoading: isLoadingUserMaterial } = useContext(UserMaterialContext);
  const { slideSets, isLoading: isLoadingSlideSets } = useSlideSets();
  const { selectedNode } = useContext(TableOfContentsContext);
  const { t } = useTranslation();
  const isLoading = isLoadingUserMaterial || isLoadingSlideSets || isLoadingMediaLinks;

  if (selectedModule?.isExerciseModule) {
    const { exerciseMedialinks, nonExerciseMedialinks } = splitExerciseMedialinks(medialinks);

    const isIntermediateLevel = (selectedNode?.nodes?.length ?? 0) > 0;
    const showExerciseSection = !isIntermediateLevel || exerciseMedialinks.length > 0;
    const showOtherSection =
      !isIntermediateLevel || nonExerciseMedialinks.length > 0 || userMaterial.length > 0 || slideSets.length > 0;

    return (
      <div className="material-sections">
        {showExerciseSection && (
          <MaterialSection
            title={t(`${BASE}.EXERCISE_MODULE.EXERCISE_MATERIAL.TITLE`)}
            medialinks={exerciseMedialinks}
            actions={false}
            isLoading={isLoading}
            subLocationId={ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_EXERCISE_MODULE_EXERCISE_MATERIAL}
            isExerciseModule={selectedModule?.isExerciseModule}
            emptyMessage={t(`${BASE}.EXERCISE_MODULE.EXERCISE_MATERIAL.EMPTY_MESSAGE`)}
          />
        )}
        {showOtherSection && (
          <MaterialSection
            title={t(`${BASE}.EXERCISE_MODULE.OTHER_MATERIAL.TITLE`)}
            medialinks={nonExerciseMedialinks}
            userMaterial={userMaterial}
            slideSets={slideSets}
            isLoading={isLoading}
            subLocationId={ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_EXERCISE_MODULE_OTHER_MATERIAL}
            isExerciseModule={selectedModule?.isExerciseModule}
            emptyMessage={t(`${BASE}.EMPTY_DIALOG.TEXT`)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="material-section">
      <MaterialSection
        title={t(`${BASE}.TITLE`)}
        medialinks={medialinks}
        userMaterial={userMaterial}
        slideSets={slideSets}
        isLoading={isLoading}
        subLocationId={ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_MODULE_OTHER_MATERIAL}
      />
    </div>
  );
}
